import React from "react";
import Downshift from "downshift";
import withStyles from "@material-ui/core/styles/withStyles";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import TextField from "@material-ui/core/TextField/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CloseIcon from "@material-ui/icons/Close";

const styles = theme => ({
  clickableIcon: {
    cursor: "pointer",
    transform: "none"
  },
  downArrowIcon: {
    fontSize: theme.typography.pxToRem(22)
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(18)
  },
  rotate: {
    transform: "rotate(180deg)"
  },
  menuContainer: {
    position: "relative",
    zIndex: theme.zIndex.modal + 1000
  },
  selectMenu: {
    maxHeight: 355,
    overflow: "auto",
    position: "absolute",
    width: "100%",
    zIndex: theme.zIndex.modal
  },
  disabled: {
    cursor: "not-allowed"
  },
  fullWidth: {
    width: "100%"
  }
});

const OSSTypeahead = ({
  classes,
  items = [],
  TextFieldProps = {},
  MenuItemProps = {},
  InputProps: GivenInputProps = {},
  placeholder = "Select",
  onSelect = () => {},
  disabledItem = () => {},
  renderItem = item => String(item),
  matchKey,
  selectedItem,
  fullWidth = false
}) => {
  const renderInput = inputProps => {
    const {
      clearSelection,
      getToggleButtonProps,
      InputProps,
      isOpen,
      openMenu,
      ref,
      selectedItem,
      ...other
    } = inputProps;

    const { disabled } = InputProps;

    return (
      <TextField
        variant="outlined"
        margin="dense"
        onFocus={openMenu}
        InputProps={{
          inputRef: ref,
          endAdornment: (
            <InputAdornment position="end">
              {selectedItem ? (
                <CloseIcon
                  className={[
                    classes.clickableIcon,
                    classes.closeIcon,
                    disabled ? classes.disabled : ""
                  ].join(" ")}
                  onClick={disabled ? () => {} : clearSelection}
                />
              ) : (
                <KeyboardArrowDownIcon
                  className={[
                    classes.clickableIcon,
                    classes.downArrowIcon,
                    isOpen ? classes.rotate : ""
                  ].join(" ")}
                  {...getToggleButtonProps()}
                />
              )}
            </InputAdornment>
          ),
          ...InputProps
        }}
        {...TextFieldProps}
        {...other}
      />
    );
  };

  const getSuggestions = (value = "") => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    let count = 0;

    return inputLength === 0
      ? items
      : items.filter(suggestion => {
          const matchValue = matchKey ? suggestion[matchKey] : suggestion;
          const keep =
            count < 5 &&
            matchValue.slice(0, inputLength).toLowerCase() === inputValue;

          if (keep) {
            count += 1;
          }

          return keep;
        });
  };

  const renderSuggestion = ({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectItem
  }) => {
    const isHighlighted = highlightedIndex === index;

    const handleSelect = () => {
      if (disabledItem(suggestion)) {
        return;
      }

      selectItem(matchKey ? suggestion[matchKey] : suggestion);
    };

    return (
      <MenuItem
        {...MenuItemProps}
        key={index}
        selected={isHighlighted}
        component="li"
        onClick={() => handleSelect}
        {...itemProps}
      >
        {renderItem(suggestion)}
      </MenuItem>
    );
  };

  const handleStateChange = changes => {
    const hasSelectedItem = changes.hasOwnProperty("selectedItem");

    if (hasSelectedItem) {
      onSelect(changes.selectedItem);
    }
  };

  /**
   * Returns the value of a given item.
   * @param {*} item the given item
   * @param {Boolean} asString whether to return the item as a string
   */
  const getItemValue = (item, asString = false) => {
    if (item) {
      const matchKeyItem = item[matchKey];

      if (matchKey && matchKeyItem) {
        return asString ? String(matchKeyItem) : matchKeyItem;
      }

      return asString ? String(item) : item;
    }

    return "";
  };

  return (
    <Downshift
      selectedItem={getItemValue(selectedItem)}
      itemToString={i => getItemValue(i, true)}
      onStateChange={handleStateChange}
      className={fullWidth && classes.fullWidth}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        inputValue,
        isOpen,
        openMenu,
        selectedItem,
        selectItem,
        clearSelection,
        getToggleButtonProps
      }) => {
        const inputProps = {
          fullWidth: true,
          getToggleButtonProps,
          isOpen,
          selectedItem,
          clearSelection,
          openMenu,
          InputProps: getInputProps({
            ...GivenInputProps,
            placeholder
          })
        };

        return (
          <div>
            {renderInput(inputProps)}
            <div {...getMenuProps()} className={classes.menuContainer}>
              {isOpen && (
                <Paper square className={classes.selectMenu}>
                  {getSuggestions(inputValue).map((suggestion, index) => {
                    const item = !disabledItem(suggestion) ? suggestion : null;

                    return renderSuggestion({
                      suggestion,
                      index,
                      selectItem,
                      itemProps: getItemProps({
                        item,
                        disabled: disabledItem(suggestion)
                      }),
                      highlightedIndex
                    });
                  })}
                </Paper>
              )}
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};

export default withStyles(styles)(OSSTypeahead);
